/**
 * Boot state machine — controls the loading screen → app transition.
 *
 * States: checking → auth → holding → ready → app | error
 *
 * 1. checking  — health endpoint polled, platform facts cycled
 * 2. auth      — portal session bridge attempted (cookie → JWT)
 * 3. holding   — if auth fails, show magic link form
 * 4. ready     — JWT confirmed, runtime config loaded
 * 5. app       — mount the main SPA
 * 6. error     — unrecoverable failure
 */

export type BootState = "checking" | "auth" | "holding" | "ready" | "app" | "error";

export interface BootContext {
  state: BootState;
  health: any | null;
  jwt: string | null;
  tenantId: string | null;
  role: "client" | "principal" | null;
  error: string | null;
  facts: string[];
}

// Static company facts cycled during loading
export const STATIC_FACTS: string[] = [
  "This portal is your Spec — inhabited, not downloaded.",
  "Your agent employee compounds coverage into a walk-away book.",
  "Outreach campaigns feed Highlights when impact moments land.",
  "Every action is receipted. Every outcome is proven.",
  "Day 30: convey the Spec to your own metal — still standing.",
  "Opt in → access → inhabit. The Spec deepens with you.",
];

export async function checkHealth(): Promise<any> {
  try {
    const res = await fetch("/api/health", { credentials: "include" });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export function getLiveFact(health: any): string | null {
  if (!health) return null;
  if (health.service) return `Platform: ${health.service} — operational.`;
  if (health.status === "ok") return "Platform operational.";
  return null;
}

export function getAllFacts(health: any): string[] {
  const live = getLiveFact(health);
  return live ? [live, ...STATIC_FACTS] : STATIC_FACTS;
}
